import posthog from "posthog-js";

/**
 * PostHog — analityka produktowa (faza 0 strategii).
 *
 * Zasady (apka z dziećmi, RODO minimization — jak Sentry w main.tsx):
 *   - autocapture WYŁĄCZONE — zbieramy wyłącznie jawnie nazwane eventy,
 *   - session recording WYŁĄCZONE (RODO Art. 8 — dzieci),
 *   - host EU (eu.i.posthog.com) — dane nie wychodzą poza UE,
 *   - identyfikacja pseudonimowa po uid, zero imion/emaili w properties.
 *
 * Bez VITE_POSTHOG_KEY wszystko jest no-op — kod może iść na produkcję
 * zanim projekt PostHog istnieje.
 */

const KEY = import.meta.env.VITE_POSTHOG_KEY as string | undefined;
const HOST =
  (import.meta.env.VITE_POSTHOG_HOST as string | undefined) ||
  "https://eu.i.posthog.com";

let initialized = false;

export type AnalyticsEvent =
  | "user_signed_up"
  | "session_started"
  | "child_logged_in"
  | "chore_created"
  | "completion_submitted"
  | "completion_approved"
  | "payout_completed";

export function initAnalytics(): void {
  if (!KEY || initialized) return;
  posthog.init(KEY, {
    api_host: HOST,
    autocapture: false,
    capture_pageview: true,
    capture_pageleave: false,
    disable_session_recording: true,
    persistence: "localStorage",
  });
  initialized = true;
}

export function track(
  event: AnalyticsEvent,
  properties?: Record<string, string | number | boolean | null>,
): void {
  if (!initialized) return;
  posthog.capture(event, properties);
}

/** Pseudonimowa identyfikacja po uid — bez imion/emaili. */
export function identifyUser(
  uid: string,
  props?: Record<string, string | number | boolean>,
): void {
  if (!initialized) return;
  posthog.identify(uid, props);
}

/**
 * Super properties — doklejane do każdego eventu (rola, skład rodziny).
 * Pozwala segmentować D30 po childrenCount / parentsCount bez joinów.
 */
export function registerSuperProps(
  props: Record<string, string | number | boolean>,
): void {
  if (!initialized) return;
  posthog.register(props);
}

/** Przy signOut — nie sklejaj kolejnego usera na shared device. */
export function resetAnalytics(): void {
  if (!initialized) return;
  posthog.reset();
}

// ── signup_source ──────────────────────────────────────────────────────
// ?ref= / ?utm_source= łapane przy pierwszym wejściu na JAKĄKOLWIEK stronę
// i trzymane w localStorage do momentu signupu. First-touch attribution —
// kolejne wejścia nie nadpisują.

const SOURCE_KEY = "kd_signup_source";

export function captureSignupSource(): void {
  try {
    const params = new URLSearchParams(window.location.search);
    const src = params.get("ref") || params.get("utm_source");
    if (src && !localStorage.getItem(SOURCE_KEY)) {
      localStorage.setItem(SOURCE_KEY, src.slice(0, 50));
    }
  } catch {
    // Prywatny tryb / brak localStorage — attribution to nice-to-have.
  }
}

export function getSignupSource(): string | null {
  try {
    return localStorage.getItem(SOURCE_KEY);
  } catch {
    return null;
  }
}
